<script setup lang="ts">
import { useFormContext } from 'vee-validate'
import { onMounted, ref } from 'vue'

import type { DropdownOption } from '@/components/common/ui/inputs/inputTypes'

import { TEST_IDS_CREATE } from '@/_testIds/testIdsCreate'
import ButtonSecondary from '@/components/common/ui/buttons/ButtonSecondary.vue'
import CheckBox from '@/components/common/ui/inputs/CheckBox.vue'
import SingleSelect from '@/components/common/ui/inputs/SingleSelect.vue'
import TextInput from '@/components/common/ui/inputs/TextInput.vue'
import TWTextArea from '@/components/common/ui/inputs/TWTextArea.vue'
import SectionMetaData from '@/components/editRelationGroup/sections/SectionMetaData.vue'
import { RelationType } from '@/constants/relations'
import { useFrappeNestCustomAPIStore } from '@/stores/apis/frappeNestCustomAPI'
import { useCreateRelationSlideInStore } from '@/stores/createRelationSlideInStore.ts'
import { translationFunctionDefault } from '@/utilities/translations'

interface CreateOrganizationFormShortProps {
  doctypeOptions: DropdownOption<RelationType>[]
  isPersonCustomer: boolean
  onShowAllFields(): void
  aiLoading?: boolean | undefined
}

const props = defineProps<CreateOrganizationFormShortProps>()
const emit = defineEmits<{
  aiCancel: []
  aiClick: [organizationName: string]
  nameChange: []
  'update:isPersonCustomer': [value: boolean]
}>()

const { onShowAllFields } = props

const { values } = useFormContext()
const investorTypeOptions = ref<DropdownOption<string>[]>([])
const otherTypeOptions = ref<DropdownOption<string>[]>([])
const customAPIStore = useFrappeNestCustomAPIStore()

const handleAiClick = (): void => {
  emit('aiClick', values['organization_name'])
}

const handleAiCancel = (): void => {
  emit('aiCancel')
}

const handleNameChange = (): void => {
  emit('nameChange')
}

const tfDefault = translationFunctionDefault()

onMounted(async () => {
  try {
    const types = await customAPIStore.getTWInvestorType()
    investorTypeOptions.value = types.map((it) => ({
      optionLabel: tfDefault(`investor.information.investor_type_options.${it.name}`),
      optionValue: it.name,
    }))
  } catch {
    /* empty */
  }
  try {
    const otypes = await customAPIStore.getTWOtherType()
    otherTypeOptions.value = otypes.map((ot) => ({ optionLabel: ot.name, optionValue: ot.name }))
  } catch {
    /* empty */
  }
})

const createRelationSlideInStore = useCreateRelationSlideInStore()
const { organisationRelationType } = createRelationSlideInStore

const onPersonCustomerChange = (): void => {
  emit('update:isPersonCustomer', !props.isPersonCustomer)
}
</script>

<template>
  <div class="space-y-5 px-8">
    <TextInput
      ai-cancelable
      :ai-loading="aiLoading"
      :capitalize-first="false"
      :data-testid="TEST_IDS_CREATE.createOrganizationForm.organizationName"
      :label="
        props.isPersonCustomer
          ? tfDefault('createOrganization.labelPersonName')
          : tfDefault('createOrganization.labelOrganizationName')
      "
      name="organization_name"
      on-white-bg
      required
      :show-ai-button="!!values['organization_name'] && !props.isPersonCustomer"
      @ai-cancel="handleAiCancel"
      @ai-click="handleAiClick"
      @blur="handleNameChange"
      @manual-input="handleNameChange"
    />
    <SingleSelect
      :data-testid="TEST_IDS_CREATE.createOrganizationForm.organizationType"
      :label="tfDefault('createOrganization.labelRelationType')"
      name="doctype"
      on-white-bg
      :options="[doctypeOptions]"
      :preselect-value="organisationRelationType"
      required
    />
    <CheckBox
      v-if="values['doctype'] === RelationType.customers"
      id="is-person-customer"
      :checked="props.isPersonCustomer"
      :label-text="tfDefault('createOrganization.isPersonCustomerCheckbox')"
      @change="onPersonCustomerChange"
    />
    <!-- Investors (Bank) specific fields -->
    <SingleSelect
      v-if="values['doctype'] === RelationType.investors"
      :label="tfDefault('investor.information.investor_type')"
      name="tw_investor_type"
      on-white-bg
      :options="[investorTypeOptions]"
      :placeholder="tfDefault('investor.information.noInvestorType')"
    />
    <TWTextArea
      v-if="values['doctype'] === RelationType.investors"
      :label="tfDefault('investor.information.loanDescription')"
      name="tw_description"
      on-white-bg
    />
    <TextInput
      v-if="values['doctype'] === RelationType.investors"
      is-number
      :label="tfDefault('investor.information.totalLoanAmount')"
      name="tw_total_loan_amount"
      on-white-bg
    />

    <!-- Other (TW Other) specific fields -->
    <SingleSelect
      v-if="values['doctype'] === RelationType.other"
      key="single-select-2"
      :label="tfDefault('other.information.type')"
      name="type"
      on-white-bg
      :options="[otherTypeOptions]"
      :placeholder="tfDefault('other.information.noType')"
    />

    <TextInput
      v-if="values['doctype'] === RelationType.salesPartners"
      key="text-input-2"
      is-number
      :label="tfDefault('salesPartner.details.commissionRate')"
      name="commission_rate"
      on-white-bg
    />
  </div>
  <SectionMetaData class="border-none" />
  <div class="flex w-full justify-center">
    <ButtonSecondary
      class="mt-5"
      :data-testid="TEST_IDS_CREATE.createOrganizationForm.showAllFields"
      :label="tfDefault('createRelation.showAllFields')"
      small
      @click="onShowAllFields"
    >
    </ButtonSecondary>
  </div>
</template>
